/**
 * HubEmpty — the slide-over's "no source library yet" state.
 *
 * Rendered when the workspace has no libraries, OR when the writer
 * lands in the Hub with no active library selected. Big primary
 * CTA: "Create library". Hint underneath: "or drag files anywhere
 * on this pane" (Spec 34 §"Implementation milestones" M5 wires the
 * drag-drop target).
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

export interface HubEmptyProps {
  onCreate: () => void;
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 24px",
    textAlign: "center",
    backgroundColor: surfaceColors.paper,
  },
  illustration: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: brandColors.light,
    border: `2px dashed ${brandColors.mid}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "14px",
    fontWeight: 700,
    fontFamily: fonts.display,
    color: inkColors.ink,
    marginBottom: "4px",
  },
  blurb: {
    fontSize: "11px",
    color: inkColors.ink3,
    lineHeight: 1.5,
    maxWidth: "280px",
    marginBottom: "18px",
  },
  cta: {
    backgroundColor: brandColors.primary,
    color: "white",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "11px",
    fontWeight: 700,
    fontFamily: fonts.body,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    cursor: "pointer",
    ":hover": { opacity: 0.9 },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  dragHint: {
    marginTop: "14px",
    fontSize: "9px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
  },
});

export const HubEmpty: React.FC<HubEmptyProps> = ({ onCreate }) => {
  const styles = useStyles();
  return (
    <div className={styles.root} data-testid="hub-empty">
      <div className={styles.illustration} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"
            stroke={brandColors.primary}
            strokeWidth="1.6"
          />
          <path d="M12 11v6m-3-3h6" stroke={brandColors.primary} strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>
      <div className={styles.title}>No source library yet</div>
      <div className={styles.blurb}>
        The drafting agent writes from the sources you give it. Create a library, drop in protocols, SAPs, TLFs, batch records —
        I&apos;ll classify and tag them.
      </div>
      <button type="button" onClick={onCreate} className={styles.cta}>
        + Create library
      </button>
      <div className={styles.dragHint}>or drag files anywhere on this pane</div>
    </div>
  );
};

export default HubEmpty;
